import React, { useState, useRef } from 'react';
import {
    Typography,
    Card,
    Button,
    Space,
    Form,
    Input,
    DatePicker,
    Switch,
    InputNumber,
    message,
    Upload,
    Alert,
    Divider,
    Table,
    Tag,
    Row,
    Col,
    Modal,
    Progress,
    Radio
} from 'antd';
import dayjs from 'dayjs';
import {
    ArrowLeftOutlined,
    FileExcelOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    CheckCircleFilled,
    AppstoreAddOutlined,
    InfoCircleOutlined,
    LoadingOutlined,
    SendOutlined
} from '@ant-design/icons';
import * as XLSX from 'xlsx';
import { TrainingType } from '../types/training';
import {
    createMoodleProgram,
    createMoodleQuiz,
    getMoodleProgramDetails,
    // adminFetchObjectiveQuestions still needed if not ported? I'll port it or use the new one.
} from '../services/moodleService';
import { parseModuleId, validateBlobSafeName } from '../utils/moduleUtils';
import { useLocation, useNavigate } from 'react-router-dom';
import { getCurrentUserEmail } from '../services/trainingService';
import AICurriculumModal, { CurriculumResult } from '../components/AICurriculumModal';
import { RobotOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;

interface ModuleEntry {
    id: string;
    name: string;
    questions: any[];
    fileName: string;
    estimatedTime: number; // New field
}

const CreateObjectiveProgram: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [modules, setModules] = useState<ModuleEntry[]>([]);

    // Progress Modal State
    const [progressModalVisible, setProgressModalVisible] = useState(false);
    const [currentStep, setCurrentStep] = useState(0);
    const [totalSteps, setTotalSteps] = useState(0);
    const [progressMessage, setProgressMessage] = useState('');
    const [processStatus, setProcessStatus] = useState<'active' | 'success' | 'exception'>('active');

    // AI Curriculum Designer State
    const [curriculumModalOpen, setCurriculumModalOpen] = useState(false);


    const [form] = Form.useForm();
    const navigate = useNavigate();
    const location = useLocation();
    const publishIntentRef = useRef(false);

    // Watch the form target title.
    const trainingName = Form.useWatch('trainingName', form);

    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const copyFromId = params.get('copyFrom');
        if (copyFromId) {
            handleDuplicatePreFill(copyFromId);
        }
    }, [location.search]);

    const handleDuplicatePreFill = async (trainingId: string) => {
        try {
            setLoading(true);
            const data = await getMoodleProgramDetails(trainingId);
            if (!data) {
                message.error('Source program not found in Moodle');
                return;
            }

            // 1. Pre-fill form basics
            form.setFieldsValue({
                trainingName: `${data.trainingName} - Copy`,
                trainingType: data.trainingType || TrainingType.OPEN,
                passingPercentage: data.passingPercentage,
                estimatedEffortHours: data.estimatedEffortHours,
                trainingLink: data.trainingLink,
                notificationVersion: '1.0',
                // Clear restricted fields
                trainingId: '', 
                dateRange: null,
                isEvergreen: true, // Defaulting for simple POC
                audienceEmails: data.audienceEmails,
                stakeholderEmails: data.stakeholderEmails
            });

            // 2. Map Modules (simplified for POC duplication)
            // In a full implementation, we'd fetch the course modules via Moodle API.
            setModules([]); 
            message.success(`Metadata loaded from ${data.trainingName}. Please re-add modules.`);
        } catch (error) {
            console.error('Error pre-filling duplicate:', error);
            message.error('Failed to load data from Moodle for duplication');
        } finally {
            setLoading(false);
        }
    };

    const handleFileUpload = async (file: File, moduleId: string) => {
        try {
            const data = await file.arrayBuffer();
            const workbook = XLSX.read(data);
            const worksheet = workbook.Sheets[workbook.SheetNames[0]];
            const jsonData = XLSX.utils.sheet_to_json(worksheet);

            if (jsonData.length === 0) {
                message.error('The uploaded Excel file is empty.');
                return;
            }

            // Map and Validate - Skip rows that are essentially empty or missing core fields
            const questionData = jsonData
                .map((row: any) => ({
                    Question: String(row.Question || row.question || '').trim(),
                    CodeSnippet: String(row['Code Snippet'] || row.CodeSnippet || row['Code snippet'] || '').trim(),
                    OptionA: String(row.OptionA || row.optionA || '').trim(),
                    OptionB: row.OptionB !== undefined ? String(row.OptionB).trim() : '',
                    OptionC: row.OptionC !== undefined ? String(row.OptionC).trim() : '',
                    OptionD: row.OptionD !== undefined ? String(row.OptionD).trim() : '',
                    Answer: String(row.Answer || row.answer || '').trim().toUpperCase()
                }))
                .filter(q => q.Question.length > 0 && q.Answer.length > 0);

            if (questionData.length === 0) {
                message.error('No valid questions found. Ensure "Question" and "Answer" columns are filled.');
                return;
            }

            setModules(prev => prev.map(m =>
                m.id === moduleId ? { ...m, questions: questionData, fileName: file.name } : m
            ));
            message.success(`Successfully loaded ${questionData.length} questions for module.`);
        } catch (error) {
            message.error('Failed to parse Excel file.');
        }
    };

    const addModuleRow = () => {
        const newId = Math.random().toString(36).substr(2, 9);
        setModules([...modules, { id: newId, name: '', questions: [], fileName: '', estimatedTime: 30 }]);
    };

    const removeModuleRow = (id: string) => {
        setModules(modules.filter(m => m.id !== id));
    };

    const updateModuleField = (id: string, field: keyof ModuleEntry, value: any) => {
        setModules(prev => prev.map(m => m.id === id ? { ...m, [field]: value } : m));
    };

    const handleApplyCurriculum = (aiPlan: CurriculumResult) => {
        // Map the AI suggestions to internal ModelEntry rows
        // Objective modules don't display descriptions in the grid, just the name
        const newModules = aiPlan.modules.map(mod => ({
            id: Math.random().toString(36).substr(2, 9),
            name: mod.name,
            questions: [],
            fileName: '',
            estimatedTime: 30 // Default
        }));

        setModules(prev => [...prev, ...newModules]);

        // Auto-fill the trainingLink if provided AND it's not currently populated
        const currentLink = form.getFieldValue('trainingLink');
        if (aiPlan.suggestedLink && !currentLink) {
            form.setFieldsValue({ trainingLink: aiPlan.suggestedLink });
        }

        // Auto-fill estimatedEffortHours if provided
        if (aiPlan.estimatedEffortHours) {
            form.setFieldsValue({ estimatedEffortHours: aiPlan.estimatedEffortHours });
        }
    };

    const handleFinish = async (values: any) => {
        try {
            if (modules.length === 0) {
                throw new Error('Please add at least one module and assessment.');
            }

            const modulesWithNoQs = modules.filter(m => m.questions.length === 0 || !m.name.trim());
            if (modulesWithNoQs.length > 0) {
                throw new Error('All modules must have a name and an uploaded assessment file.');
            }

            // valid
            setTotalSteps(modules.length + 1); // 1 for Create Training, N for Modules
            setCurrentStep(0);
            setProcessStatus('active');
            setProgressMessage('Initializing creation process...');
            setProgressModalVisible(true);
            setLoading(true);

            // 1. Create the Training Program
            const cleanEmails = (str: string) => str ? str.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean).join(';') : '';

            const trainingPayload = {
                trainingId: values.trainingId,
                trainingName: values.trainingName,
                active: true,
                startDate: values.isEvergreen ? null : values.dateRange[0].format('YYYY-MM-DD'),
                endDate: values.isEvergreen ? null : values.dateRange[1].format('YYYY-MM-DD'),
                passingPercentage: values.passingPercentage,
                trainingLink: values.trainingLink,
                estimatedEffortHours: values.estimatedEffortHours,
                audienceEmails: cleanEmails(values.audienceEmails),
                stakeholderEmails: cleanEmails(values.stakeholderEmails),
                assessments: modules.map(m => `${m.name.trim()} #T${m.estimatedTime || 30}`).join(' | '),
                notificationVersion: values.notificationVersion || '1.0',
                trainingType: values.trainingType,
                visible: publishIntentRef.current ? 1 : 0,
                createdby: await getCurrentUserEmail()
            };

            setProgressMessage('Creating Training Program in Moodle...');
            const programResult = await createMoodleProgram(trainingPayload as any);
            const courseId = programResult.id;
            setCurrentStep(1);

            // 2. Create Moodle Quizzes for each module
            for (let i = 0; i < modules.length; i++) {
                const module = modules[i];
                setProgressMessage(`Creating Moodle Quiz for module: ${module.name} (${i + 1}/${modules.length})...`);
                
                // In this Headless POC, the Excel upload content is ignored for now as we focus on the structure.
                // A future phase will parse the questions and insert into Moodle's question bank.
                await createMoodleQuiz(
                    courseId, 
                    module.name, 
                    `Assessment for ${module.name}.`,
                    i + 1, // Add to specific section
                    module.questions,
                    module.estimatedTime || 30
                );
                
                setCurrentStep(prev => prev + 1);
            }

            setProcessStatus('success');
            setProgressMessage(publishIntentRef.current
                ? 'Program published successfully! Redirecting...'
                : 'Draft saved! You can publish it later from the admin dashboard. Redirecting...');

            setTimeout(() => {
                navigate('/admin');
            }, 1500);

        } catch (error: any) {
            setProcessStatus('exception');
            setProgressMessage(`Error: ${error.message || 'Failed to create training program'}`);
            setLoading(false);
            // We keep modal open so user can see error
        }
    };

    const handleCloseModal = () => {
        if (processStatus === 'exception') {
            setProgressModalVisible(false);
        }
        // otherwise locked
    };

    const columns = [
        {
            title: 'Module / Submodule Name',
            dataIndex: 'name',
            key: 'name',
            render: (_: any, record: ModuleEntry) => (
                <Input
                    placeholder="e.g. Introduction to React"
                    value={record.name}
                    onChange={(e) => updateModuleField(record.id, 'name', e.target.value)}
                    style={{ borderRadius: '6px' }}
                />
            )
        },
        {
            title: 'Time (Mins)',
            dataIndex: 'estimatedTime',
            width: 100,
            render: (_: any, record: ModuleEntry) => (
                <InputNumber
                    min={1}
                    max={300}
                    value={record.estimatedTime}
                    onChange={(val) => updateModuleField(record.id, 'estimatedTime', val || 30)}
                    style={{ width: '100%' }}
                />
            )
        },
        {
            title: 'Assessment File',
            dataIndex: 'fileName',
            key: 'fileName',
            render: (_: any, record: ModuleEntry) => (
                <Space>
                    <Upload
                        beforeUpload={(file) => {
                            handleFileUpload(file, record.id);
                            return false;
                        }}
                        showUploadList={false}
                        accept=".xlsx,.xls"
                    >
                        <Button icon={<FileExcelOutlined />} type={record.fileName ? 'default' : 'dashed'}>
                            {record.fileName ? 'Change File' : 'Upload Excel'}
                        </Button>
                    </Upload>
                    {record.fileName ? (
                        <Space>
                            <Tag color="success" icon={<CheckCircleFilled />}>Loaded</Tag>
                            <Text type="secondary" style={{ fontSize: '12px' }}>({record.questions.length} Qs)</Text>
                        </Space>
                    ) : (
                        <Tag icon={<InfoCircleOutlined />} color="default">Required</Tag>
                    )}
                </Space>
            )
        },
        {
            title: 'Action',
            key: 'action',
            width: 80,
            render: (_: any, record: ModuleEntry) => (
                <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => removeModuleRow(record.id)}
                />
            )
        }
    ];

    const percent = Math.round((currentStep / Math.max(totalSteps, 1)) * 100);

    return (
        <div style={{ padding: '24px', maxWidth: '1000px', margin: '0 auto' }}>

            {/* Header */}
            <div style={{ marginBottom: '24px' }}>
                <Button
                    icon={<ArrowLeftOutlined />}
                    onClick={() => navigate('/admin')}
                    style={{ marginBottom: '16px' }}
                >
                    Back to Admin
                </Button>
                <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)', border: '1px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{
                            width: 64, height: 64, borderRadius: '16px',
                            backgroundColor: '#fff7e6',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            marginRight: 24
                        }}>
                            <AppstoreAddOutlined style={{ fontSize: '32px', color: '#fa541c' }} />
                        </div>
                        <div>
                            <Title level={2} style={{ margin: 0 }}>Create Training Program</Title>
                            <Text type="secondary">Define program details, add modules, and upload assessments.</Text>
                        </div>
                    </div>
                </Card>
            </div>

            <AICurriculumModal
                open={curriculumModalOpen}
                onClose={() => setCurriculumModalOpen(false)}
                onApply={handleApplyCurriculum}
                initialTopic={trainingName}
            />

            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
                initialValues={{
                    active: true,
                    passingPercentage: 80,
                    notificationVersion: '1.0',
                    trainingType: TrainingType.OPEN,
                    isEvergreen: true
                }}
            >
                <Card title="1. Program Details" style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}>
                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={8}>
                            <Form.Item label="Training ID" name="trainingId" rules={[{ required: true }, { validator: validateBlobSafeName }]}>
                                <Input placeholder="TR-202X-001" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Passing %" name="passingPercentage" rules={[{ required: true }]}>
                                <InputNumber min={0} max={100} style={{ width: '100%' }} formatter={value => (value !== undefined && value !== null && `${value}` !== '') ? `${value}%` : ''} parser={value => value ? parseFloat(value.replace('%', '')) : 0 as any} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item label="Assessment Duration (Hours)" name="estimatedEffortHours">
                                <InputNumber min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row align="bottom">
                        <Col flex="auto">
                            <Form.Item label="Training Program Name" name="trainingName" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                                <Input 
                                    placeholder="Enter full program name (e.g. Frontend Masterclass)" 
                                    size="large" 
                                    maxLength={250}
                                    showCount
                                />
                            </Form.Item>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<RobotOutlined />}
                                size="large"
                                style={{ marginLeft: 16, backgroundColor: '#722ed1', borderColor: '#722ed1' }}
                                onClick={() => setCurriculumModalOpen(true)}
                                disabled={!trainingName?.trim()}
                            >
                                ✨ Auto-Generate Curriculum
                            </Button>
                        </Col>
                    </Row>
                    <br />

                    <Row gutter={[24, 0]}>
                        <Col xs={24} md={12}>
                            <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType || prevValues.isEvergreen !== currentValues.isEvergreen}>
                                {({ getFieldValue, setFieldsValue }) => {
                                    const trainingType = getFieldValue('trainingType');
                                    const isEvergreen = getFieldValue('isEvergreen');

                                    // Auto-uncheck evergreen if they switch away from Open
                                    if (trainingType !== TrainingType.OPEN && isEvergreen) {
                                        // Use setTimeout to avoid pure render synchronous state updates warning
                                        setTimeout(() => setFieldsValue({ isEvergreen: false }), 0);
                                    }

                                    return (
                                        <>
                                            {trainingType === TrainingType.OPEN && (
                                                <Form.Item name="isEvergreen" valuePropName="checked" style={{ marginBottom: 16 }}>
                                                    <Switch checkedChildren="Evergreen (No Fixed Dates)" unCheckedChildren="Fixed Dates" />
                                                </Form.Item>
                                            )}

                                            {!isEvergreen || trainingType !== TrainingType.OPEN ? (
                                                <Form.Item
                                                    label="Training Period"
                                                    name="dateRange"
                                                    rules={[{ required: true, message: 'Please select a training period' }]}
                                                    extra="Users can only start training on or after the start date."
                                                >
                                                    <DatePicker.RangePicker
                                                        style={{ width: '100%' }}
                                                        size="large"
                                                    />
                                                </Form.Item>
                                            ) : (
                                                <Alert
                                                    message="Evergreen Program"
                                                    description="This training has no fixed dates and will be available immediately."
                                                    type="info"
                                                    showIcon
                                                    style={{ marginBottom: 24 }}
                                                />
                                            )}
                                        </>
                                    );
                                }}
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item label="Resource / Documentation Link" name="trainingLink" rules={[{ required: true }]}>
                                <TextArea autoSize={{ minRows: 2, maxRows: 6 }} placeholder="https://..." style={{ borderRadius: '8px' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Divider />

                    <Form.Item label="Training Accessibility Type" name="trainingType" rules={[{ required: true }]}>
                        <Radio.Group>
                            <Space direction="vertical">
                                <Radio value={TrainingType.OPEN}>
                                    <Text strong>Open for All</Text>
                                    <br />
                                    <Text type="secondary">No nominations required. Anyone can join and take the assessment anytime.</Text>
                                </Radio>
                                <Radio value={TrainingType.REQUESTABLE}>
                                    <Text strong>Nominated + Request to Join</Text>
                                    <br />
                                    <Text type="secondary">Specific people are nominated. Others can request to join before the start date.</Text>
                                </Radio>
                                <Radio value={TrainingType.PRIVATE}>
                                    <Text strong>Nominated Only (Private)</Text>
                                    <br />
                                    <Text type="secondary">Only specific people nominated by the admin can access this training.</Text>
                                </Radio>
                            </Space>
                        </Radio.Group>
                    </Form.Item>

                    <Divider />

                    <Form.Item noStyle shouldUpdate={(prevValues, currentValues) => prevValues.trainingType !== currentValues.trainingType}>
                        {({ getFieldValue }) => {
                            const type = getFieldValue('trainingType');
                            if (type === TrainingType.OPEN) return null;
                            return (
                                <Row gutter={[24, 0]}>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Nominated Participants (Emails, semicolon separated)"
                                            name="audienceEmails"
                                            rules={[{ required: true, message: 'Participant emails are required' }]}
                                        >
                                            <TextArea rows={4} placeholder="user1@company.com; user2@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                    <Col xs={24} md={12}>
                                        <Form.Item
                                            label="Stakeholders (Emails, semicolon separated)"
                                            name="stakeholderEmails"
                                            rules={[{ required: true, message: 'Stakeholder emails are mandatory' }]}
                                        >
                                            <TextArea rows={4} placeholder="manager@company.com; director@company.com" style={{ borderRadius: '8px' }} />
                                        </Form.Item>
                                    </Col>
                                </Row>
                            );
                        }}
                    </Form.Item>
                </Card>

                <Card
                    title="2. Modules & Assessments"
                    style={{ marginBottom: '24px', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
                    extra={
                        <Button type="dashed" onClick={addModuleRow} icon={<PlusOutlined />} style={{ borderColor: '#fa541c', color: '#fa541c' }}>
                            Add Module
                        </Button>
                    }
                >
                    <Alert
                        message="Assessment Requirement"
                        description="At least one module must be added. Each module requires a name and an Excel file containing assessment questions."
                        type="info"
                        showIcon
                        style={{ marginBottom: '24px', borderRadius: '8px' }}
                    />

                    <Table
                        dataSource={modules}
                        columns={columns}
                        rowKey="id"
                        pagination={false}
                        locale={{ emptyText: <div style={{ padding: '20px', color: '#999' }}>Click "Add Module" to start configuring the curriculum.</div> }}
                        bordered
                    />
                </Card>

                <div style={{ textAlign: 'right', marginTop: '32px', marginBottom: '50px' }}>
                    <Space size="large">
                        <Button onClick={() => navigate('/admin')} size="large" disabled={loading}>Cancel</Button>
                        <Button
                            size="large"
                            icon={<SaveOutlined />}
                            disabled={loading}
                            onClick={() => { publishIntentRef.current = false; form.submit(); }}
                        >
                            Save as Draft
                        </Button>
                        <Button
                            type="primary"
                            size="large"
                            icon={<SendOutlined />}
                            loading={loading}
                            style={{ backgroundColor: '#fa541c', borderColor: '#fa541c', color: '#fff' }}
                            onClick={() => { publishIntentRef.current = true; form.submit(); }}
                        >
                            Publish
                        </Button>
                    </Space>
                </div>
            </Form>

            {/* Progress / Blocking Modal */}
            <Modal
                title={
                    <Space>
                        {processStatus === 'active' && <LoadingOutlined />}
                        <span>Processing Training Program</span>
                    </Space>
                }
                open={progressModalVisible}
                closable={processStatus === 'exception'}
                maskClosable={false}
                onCancel={processStatus === 'exception' ? handleCloseModal : undefined}
                footer={processStatus === 'exception' ? [
                    <Button key="close" onClick={handleCloseModal}>Close</Button>
                ] : null}
                centered
            >
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Progress
                        type="circle"
                        percent={percent}
                        status={processStatus}
                        strokeColor="#fa541c"
                    />
                    <div style={{ marginTop: '24px' }}>
                        <Title level={4}>{processStatus === 'success' ? 'Completed!' : 'Creating & Uploading...'}</Title>
                        <Text>{progressMessage}</Text>
                    </div>
                    {processStatus === 'active' && (
                        <Alert
                            message="Do not close this window"
                            description="Uploading large assessment files may take a moment. Please wait."
                            type="warning"
                            showIcon
                            style={{ marginTop: '24px', textAlign: 'left' }}
                        />
                    )}
                </div>
            </Modal>
        </div>
    );
};

export default CreateObjectiveProgram;
